/* eslint-disable react-hooks/rules-of-hooks */
import dashboardData from 'layouts/authenticated/pages/dashboard/data/dashboardData'
import { IntakeContext } from 'context/IntakeContext'
import { useDashboardCalculations } from 'layouts/authenticated/pages/dashboard/hooks/useDashboardCalculations'
import { selectData } from 'layouts/onboarding'
import { useState, useContext, useEffect } from 'react'
import {
  format,
  addYears,
  addDays,
  addMonths,
  max,
  min,
  differenceInMonths,
} from 'date-fns'

export const UpdatedCalcs = ({ assets, liabilities }) => {
  try {
    const { intakeState } = useContext(IntakeContext)
    const {
      personalDetailsCurrentAge,
      eligibilityScenariosFirstFullyEligibleDate,
      netIncomeTodayCalculated,
      gapCalculated,
      netIncomeInRetirementCalculated,
      atRetirementGrossIncome,
      eligibilityScenariosModeledCYSinRetirement,
      socialSecurityEstimatedSocialSecurityatFRA,
      socialSecurityEstimatedSocialSecurityatAge62,
      socialSecuritySocialSecurityBenefit,
      FERSYearsOfService,
      atRetirementBasicLife,
      atRetirementIncomeTaxes,
      atRetirementGrossPension,
      atRetirementFERSSupplement,
      atRetirementSocialSecurity,
      atRetirementSSTaxes,
      atRetirementFEHB,
    } = useDashboardCalculations()

    const clientRetirementAge =
      differenceInMonths(
        new Date(intakeState.dateOfPlannedRetirement),
        new Date(intakeState.dateOfBirth)
      ) / 12

    const dollarFormat = Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    })

    const mandatoryRetirementDate =
      selectData.retirementSystem.find(
        ({ value }) => value === intakeState.retirementSystem
      )?.mandatoryRetirementAge === 'Yes'
        ? 'something'
        : 'No Mandatory Retirement Age'

    const earliestFullyEligibleDate =
      new Date(eligibilityScenariosFirstFullyEligibleDate) < new Date()
        ? 'TODAY!'
        : format(eligibilityScenariosFirstFullyEligibleDate, 'MM/dd/yyyy')
    const holder = format(
      eligibilityScenariosFirstFullyEligibleDate,
      'MM/dd/yyyy'
    )

    // Constants
    const govSSTaxAmount = 160200
    const ssTaxPercent = 0.062
    const medicareTaxPercent = 0.0145
    // Create Pension Lookup Table to define
    const pensionContribution = 0.008

    // Little g Calcs
    const ssTaxBaseSalaryAmount = Math.min(
      intakeState.baseSalary * ssTaxPercent,
      govSSTaxAmount * ssTaxPercent
    )
    const medicareTaxBaseSalaryAmount =
      intakeState.baseSalary * medicareTaxPercent
    const pensionContributionAmount =
      intakeState.baseSalary * pensionContribution
    const tspContributionAmount =
      intakeState.baseSalary * (intakeState.tspContribution / 100)
    const littleGAmount =
      intakeState.baseSalary -
      ssTaxBaseSalaryAmount -
      medicareTaxBaseSalaryAmount -
      pensionContributionAmount -
      tspContributionAmount

    const incomePrice0 =
      dashboardData.pricetables[Math.round(personalDetailsCurrentAge)]?.[0]
        ?.male || 0
    const incomePrice1 =
      dashboardData.pricetables[Math.round(personalDetailsCurrentAge)]?.[1]
        ?.male || 0
    const incomePrice2 =
      dashboardData.pricetables[Math.round(personalDetailsCurrentAge)]?.[2]
        ?.male || 0
    const incomePrice3 =
      dashboardData.pricetables[Math.round(personalDetailsCurrentAge)]?.[3]
        ?.male || 0
    const incomePrice4 =
      dashboardData.pricetables[Math.round(personalDetailsCurrentAge)]?.[4]
        ?.male || 0
    const incomePrice5 =
      dashboardData.pricetables[Math.round(personalDetailsCurrentAge)]?.[5]
        ?.male || 0

    const riskFreeRate = 0.04523
    const jobSecurity = 0.999
    const dateDiff =
      -differenceInMonths(
        new Date(intakeState.dateOfPlannedRetirement),
        new Date()
      ) / 12
    const secondHalf = (1 - (1 + riskFreeRate) ** dateDiff) / riskFreeRate
    const capOfWorkingIncome = intakeState.baseSalary * jobSecurity * secondHalf

    const capOfPension0 =
      atRetirementGrossPension *
      dashboardData.pricetables[Math.round(clientRetirementAge)]?.[0]?.male
    const capOfPension1 =
      atRetirementGrossPension *
      dashboardData.pricetables[Math.round(clientRetirementAge)]?.[1]?.male
    const capOfPension2 =
      atRetirementGrossPension *
      dashboardData.pricetables[Math.round(clientRetirementAge)]?.[2]?.male
    const capOfPension3 =
      atRetirementGrossPension *
      dashboardData.pricetables[Math.round(clientRetirementAge)]?.[3]?.male
    const capOfPension4 =
      atRetirementGrossPension *
      dashboardData.pricetables[Math.round(clientRetirementAge)]?.[4]?.male
    const capOfPension5 =
      atRetirementGrossPension *
      dashboardData.pricetables[Math.round(clientRetirementAge)]?.[5]?.male

    const capOfFERSSupplement = ''

    const capOfSocialSecurity0 =
      atRetirementSocialSecurity *
      dashboardData.pricetables[
        Math.round(Math.max(62, clientRetirementAge))
      ]?.[0]?.male
    const capOfSocialSecurity1 =
      atRetirementSocialSecurity *
      dashboardData.pricetables[
        Math.round(Math.max(62, clientRetirementAge))
      ]?.[1]?.male
    const capOfSocialSecurity2 =
      atRetirementSocialSecurity *
      dashboardData.pricetables[
        Math.round(Math.max(62, clientRetirementAge))
      ]?.[2]?.male
    const capOfSocialSecurity3 =
      atRetirementSocialSecurity *
      dashboardData.pricetables[
        Math.round(Math.max(62, clientRetirementAge))
      ]?.[3]?.male
    const capOfSocialSecurity4 =
      atRetirementSocialSecurity *
      dashboardData.pricetables[
        Math.round(Math.max(62, clientRetirementAge))
      ]?.[4]?.male
    const capOfSocialSecurity5 =
      atRetirementSocialSecurity *
      dashboardData.pricetables[
        Math.round(Math.max(62, clientRetirementAge))
      ]?.[5]?.male

    const subtotalOfCapValues0 =
      capOfWorkingIncome + capOfPension0 + capOfSocialSecurity0

    const subtotalOfCapValues1 =
      capOfWorkingIncome + capOfPension1 + capOfSocialSecurity1

    const subtotalOfCapValues2 =
      capOfWorkingIncome + capOfPension2 + capOfSocialSecurity2

    const subtotalOfCapValues3 =
      capOfWorkingIncome + capOfPension3 + capOfSocialSecurity3

    const subtotalOfCapValues4 =
      capOfWorkingIncome + capOfPension4 + capOfSocialSecurity4

    const subtotalOfCapValues5 =
      capOfWorkingIncome + capOfPension5 + capOfSocialSecurity5

    const hasThriftSavingsPlan = assets?.some(
      (item) =>
        item.externalAsset === true &&
        item.externalInstitutionName === 'Thrift Savings Plan'
    )

    const valueOfAssets =
      (hasThriftSavingsPlan
        ? 0
        : (intakeState?.tspBalance || 0) + (intakeState?.tspRothBalance || 0)) +
      (assets || [])
        .reduce((filtered, option) => {
          if (
            option.subType === 'checkingsavings' ||
            option.subType === 'investment'
          ) {
            var newList = {
              name: option.name,
              amount: option.amount,
            }
            filtered.push(newList)
          }
          return filtered
        }, [])
        .map((assets) => assets.amount)
        .reduce((a, b) => a + b, 0) -
      (liabilities || [])
        .reduce((filtered, option) => {
          if (option.subType === 'creditcard') {
            var newList = {
              name: option.name,
              amount: option.amount,
            }
            filtered.push(newList)
          }
          return filtered
        }, [])
        .map((liabilities) => liabilities.amount)
        .reduce((a, b) => a + b, 0)

    const valueOfInvestableAssets =
      (hasThriftSavingsPlan
        ? 0
        : (intakeState?.tspBalance || 0) + (intakeState?.tspRothBalance || 0)) +
      (assets || [])
        .reduce((filtered, option) => {
          if (
            option.subType === 'checkingsavings' ||
            option.subType === 'investment'
          ) {
            var newList = {
              name: option.name,
              amount: option.amount,
            }
            filtered.push(newList)
          }
          return filtered
        }, [])
        .map((assets) => assets.amount)
        .reduce((a, b) => a + b, 0)

    // const assetSum =
    //   (assets || []).map((assets) => assets.amount).reduce((a, b) => a + b, 0) +
    //   (intakeState.tspBalance ? intakeState.tspBalance : 0) +
    //   (intakeState.tspRothBalance ? intakeState.tspRothBalance : 0)

    // const liabilitySum = (liabilities || [])
    //   .map((liabilities) => liabilities.amount)
    //   .reduce((a, b) => a + b, 0)

    // const netWorth = assetSum - liabilitySum

    const totalCapValue0 = valueOfAssets + subtotalOfCapValues0
    const totalCapValue1 = valueOfAssets + subtotalOfCapValues1
    const totalCapValue2 = valueOfAssets + subtotalOfCapValues2
    const totalCapValue3 = valueOfAssets + subtotalOfCapValues3
    const totalCapValue4 = valueOfAssets + subtotalOfCapValues4
    const totalCapValue5 = valueOfAssets + subtotalOfCapValues5

    const readinessScore0 = Math.round(
      (totalCapValue0 / (littleGAmount / (1 / incomePrice0))) * 100
    )
    const readinessScore1 = Math.round(
      (totalCapValue1 / (littleGAmount / (1 / incomePrice1))) * 100
    )
    const readinessScore2 = Math.round(
      (totalCapValue2 / (littleGAmount / (1 / incomePrice2))) * 100
    )
    const readinessScore3 = Math.round(
      (totalCapValue3 / (littleGAmount / (1 / incomePrice3))) * 100
    )
    const readinessScore4 = Math.round(
      (totalCapValue4 / (littleGAmount / (1 / incomePrice4))) * 100
    )
    const readinessScore5 = Math.round(
      (totalCapValue5 / (littleGAmount / (1 / incomePrice5))) * 100
    )

    const readinessScoreAverage =
      (readinessScore0 +
        readinessScore1 +
        readinessScore2 +
        readinessScore3 +
        readinessScore4 +
        readinessScore5) /
      6 /
      100

    const readinessScoreAdjustedMax =
      readinessScoreAverage < 0
        ? 0
        : readinessScoreAverage >= 0.95
        ? 0.95
        : readinessScoreAverage

    return {
      valueOfAssets,
      valueOfInvestableAssets,
      readinessScoreAverage,
      readinessScoreAdjustedMax,
      // netWorth,
    }
  } catch (e) {
    return {}
  }
}
